import { Box, Typography, useTheme } from '@mui/material';
import { Film, Genres } from '@/logic/services';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactNode, useCallback, useMemo } from 'react';

import { ILocale } from '@/lang/types';
import { checkIsRtl } from '@/lang/utils';
import { formatDuration } from '@/helpers';
import { pxToRem } from '@/theme/typography';
import { useRouter } from 'next/dist/client/router';

export interface FormattedFilm {
  genresWithDots: ReactNode[];
  durationWithRate: ReactNode;
  formattedDuration: string;
  durationWithRateWithRtl: JSX.Element;
}

const genresWithDots = (genres: Genres[], theme) => {
  return genres
    ?.map((genre, index: number) => [
      <Typography key={`${genre.id}-genre-name`} component='span'>
        {genre.name}
      </Typography>,
      <Typography
        key={`${genre.id}-bull`}
        component='span'
        sx={{
          color: theme.palette.layout.posterDotColor,
          marginX: pxToRem(4),
        }}
      >
        {index !== genres.length - 1 && <span>&bull;</span>}
      </Typography>,
    ])
    .flat();
};

function useFilmFormatter(movie: Film): FormattedFilm {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();
  const isRtl = checkIsRtl(locale as ILocale);

  const duration = formatDuration(movie?.runtime, intl);
  const durationWithRtl = useCallback(() => {
    return (
      <Box>
        {Math.floor(movie?.runtime / 60)} <FormattedMessage id='movie_period_hours_new' />
        &nbsp;
        {movie?.runtime % 60}
        <FormattedMessage id='movie_period_minutes_new' />
        {' . '}
        {movie?.rate?.title}
        {' . '}
        {movie?.movieLang}
      </Box>
    );
  }, [movie?.movieLang, movie?.rate?.title, movie?.runtime]);

  const output: FormattedFilm = useMemo(() => {
    const durationWithRateArr = [duration, movie?.rate?.title, movie?.movieLang];

    if (isRtl) {
      durationWithRateArr.reverse();
    }

    return {
      genresWithDots: genresWithDots(movie?.genres, theme),
      formattedDuration: duration,
      durationWithRate: (
        <bdi>&lrm;{durationWithRateArr.filter(w => !!w).join(' . ')}</bdi>
      ),
      durationWithRateWithRtl: durationWithRtl(),
    };
  }, [
    movie?.rate?.title,
    movie?.movieLang,
    movie?.genres,
    isRtl,
    theme,
    duration,
    durationWithRtl,
  ]);

  return output;
}

export default useFilmFormatter;
